import Pane from "components/Dashboards/General/Pane"
import { Button, Col, Modal, Row } from "react-bootstrap"
import { useNavigate } from "react-router"
import styles from "./editingStyles.module.scss"
import FAIcon from "components/General/FAIcon"
import { useEffect, useState } from "react"
import titleCase from "helpers/titleCase"
import { CurriculumBook, CurriculumSection, CurriculumSectionTypes, CurriculumTopic, CurriculumUnit } from "shared/types/curriculumTypes"
import { ExclamationOctagonFill } from "react-bootstrap-icons"
import { Review } from "shared/types/teacherTypes"

const { selectable, selected } = styles

type ItemType = "unit" | "topic" | "section" | "block" | "block-review-group" | "review-group" | "review"

interface BasicItemInfo {
    id: string
    name?: string
    header?: string
}

function isTopic(item: BasicItemInfo): item is CurriculumTopic {
    return (item as CurriculumTopic).sections !== undefined
}

function isSection(item: BasicItemInfo): item is CurriculumSection {
    return Object.values(CurriculumSectionTypes).includes((item as CurriculumSection).type)
}

function isReview(item: BasicItemInfo, itemType: ItemType): item is Review {
    return itemType === "review" && (item as unknown as Review).pretestId != null && (item as unknown as Review).lessonId != null
}

interface Props<T> {
    parent: CurriculumBook | CurriculumUnit | CurriculumTopic
    items: Array<T>
    selectedIndex?: number
    itemType: ItemType
    createItem: () => Promise<any>
    updateItem: (values: { id: string, order: number }) => Promise<any>
    deleteItem: (values: { id: string }) => Promise<any>
}

export default function EditingListPane<T extends BasicItemInfo>({ parent, itemType, items, selectedIndex, createItem, updateItem, deleteItem }: Props<T>) {
    const navigate = useNavigate()

    const [modal, setModal] = useState(null)
    const [deleteModalState, setDeleteModalState] = useState<{ id: string, order: number, name: string }>({ id: null, order: null, name: null })

    const [noWeightTopicIds, setNoWeightTopicIds] = useState<string[]>([])

    useEffect(() => {
        if (itemType !== "topic") return
        items.forEach(topic => {
            if (!isTopic(topic)) return
            const weightSum = topic.sections.reduce((prev, curr) => {
                prev += curr.defaultWeight
                return prev
            }, 0)
            if (weightSum === 0 && !topic.teacherOnly) setNoWeightTopicIds(prev => [...prev, topic.id])
        })
    }, [items, itemType])

    const onNavigate = (item: T, itemType: ItemType) => {
        if (itemType === "review") {
            updateItem({ id: item.id, order: null })
            return
        } else if (itemType === "review-group") {
            navigate(`${item.id}`)
            return
        } else if (itemType === "block") {
            navigate(`${item.id}`)
            return
        } else if (itemType === "block-review-group") {
            navigate(`group/${item.id}`)
            return
        }
        navigate(`${itemType}/${item.id}`)
    }

    const hideSorting = itemType === "review" || itemType === "review-group" || itemType === "block"

    return <>
        {items.map((item, index) => (
            <Row className="my-2 me-2" key={item.id}>
                <Col onClick={() => onNavigate(item, itemType)}>
                    <Pane className={`${selectable} ${index === selectedIndex ? selected : ""}`}>
                        <div className="d-flex align-items-center">
                            {itemType === "section" && isSection(item) && 
                                <div className="d-flex align-items-center fw-bold" style={{width: "50px"}}>
                                    {item.defaultWeight ? item.defaultWeight.toFixed(2) : <span className="text-muted fw-normal">0</span>}
                                </div>}
                            {itemType === "review" && isReview(item, itemType) &&
                                <div className="d-flex align-items-center fw-bold" style={{width: "50px"}}>
                                    <FAIcon iconName="check" />
                                </div>}
                                {item.name ?? item.header} 
                            {itemType === "topic" && noWeightTopicIds?.includes(item.id) && !parent.teacherOnly &&
                                <span className="d-inline-block bg-danger p-1 text-white rounded">
                                    <ExclamationOctagonFill className="me-1" />  no weights
                                </span>}
                        </div>
                    </Pane>
                </Col>
                            {!hideSorting && <>
                            <Col xs="auto">
                                <Button disabled={index === 0} onClick={async () => {
                                    updateItem({
                                        id: item.id,
                                        order: index
                                    })
                                }}><FAIcon iconName={"sort-up"} /></Button>
                            </Col>

                            <Col xs="auto">
                                <Button disabled={index === items.length - 1} onClick={async () => {
                                    updateItem({
                                        id: item.id,
                                        order: index + 2
                                    })
                                }}
                                ><FAIcon iconName={"sort-down"} /></Button>
                            </Col>
                            </>}

                            <Col xs="auto" onClick={() => {
                                setModal("deleteItem")
                                setDeleteModalState({
                                    id: item.id,
                                    name: item.name ?? item.header,
                                    order: index + 1,
                                })
                            }}>
                                <Button variant="danger"><FAIcon iconName={"skull"} /></Button>
                            </Col>
            </Row>
        ))}

        <Row>
            <Col>
                <Button onClick={createItem}><FAIcon iconName="plus" /></Button>
            </Col>
        </Row>

        <Modal show={modal === "deleteItem"} onHide={() => {
            setModal(null)
            setDeleteModalState({
                id: null,
                name: null,
                order: null
            })
        }}>
            <Modal.Header closeButton>
                Delete {titleCase(itemType)}
            </Modal.Header>

            <Modal.Body>

                <Row>
                    <h3>Are you sure you wish to delete {titleCase(itemType)} #{deleteModalState.order}, {deleteModalState.name}?</h3>
                </Row>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="danger" onClick={async () => {
                    await deleteItem({ id: deleteModalState.id })
                    setDeleteModalState({
                        id: null,
                        name: null,
                        order: null
                    })
                    setModal(null)
                }}>Delete</Button>
            </Modal.Footer>
        </Modal>
    </>
}
